import { useCallback, useEffect, useState } from "react";
import usePage from "../../../../../../../components/layouts/base-page-layout/page-hook";
import useTranslation from "../../../../../../../translation/hooks/translation-hook";
import currentPortalStore from "../../../../../../../stores/current-portal-store";
import useNavigationBlocker from "../../../../../../../hooks/navigation-blocker-hook";
import EditorPageLayout from "../../../../../../../components/layouts/editor-page/editor-page-layout";
import ControlGroupWithPreview from "../../../../../../../components/layouts/control-group-with-preview/control-group-with-preview";
import ListLayout from "../../../../../../../components/layouts/list-layout/list-layout";
import { FormFieldLine } from "../../../../../../../components/form-field-line/form-field-line";
import { Toggle } from "../../../../../../../components/inputs/toggle/toggle";
import { FormField } from "../../../../../../../components/form-field/form-field";
import TranslationInput from "../../../../../../../translation/translation-input/translation-input";
import { RETURNS_PORTAL_TRANSLATION_KEYS } from "../../../constants";
import TranslationRichTextEditor from "../../../../../../../translation/translation-rich-text-editor/translation-rich-text-editor";
import { EDITOR_VARIANT } from "../../../../../../../components/rich-text-editor/rich-text-editor";
import { ConfirmModal } from "../../../../../../../components/modal/components/confirm-modal/confirm-modal";
import PolicyConfirmationPreview from "../../../../../../../images/previews/confirmation-screen.svg";
import PolicyConfirmationPreviewPortalEdge from "../../../../../../../images/previews/policy-confirmation-preview-portal-edge.svg";
import { observer } from "mobx-react";

interface IPolicyConfirmationEditorContentProps {
    isPortalEdgeSelected: boolean;
}

const PolicyConfirmationEditorContent: React.FC<IPolicyConfirmationEditorContentProps> = observer(({ isPortalEdgeSelected }) => {
  const { setSubTitles } = usePage();
  const [isShowUnsaved, setIsShowUnsaved] = useState(false);

  useEffect(() => {
      setSubTitles([
          "Portal",
          "Content",
          "Return Portal",
          "Policy Confirmation",
      ]);
  }, [setSubTitles]);

  const {
      hasChanges: hasTranslationChanges,
      discardChanges: translationsDiscardChanges,
      save: saveTranslations,
  } = useTranslation();

  const hasChanges = () => {
      return (
          currentPortalStore.setting?.hasChanges() || hasTranslationChanges()
      );
  };

  const { retryNavigation, unblockNavigation } = useNavigationBlocker(
      () => hasChanges(),
      useCallback(() => setIsShowUnsaved(true), [setIsShowUnsaved])
  );

  const discardChanges = () => {
      currentPortalStore.setting?.discardChanges();
      translationsDiscardChanges();
  };

  const onSaveClicked = () => {
      const promised = [];

      if (currentPortalStore.setting?.hasChanges()) {
          promised.push(currentPortalStore.setting?.save());
      }

      if (hasTranslationChanges()) {
          promised.push(saveTranslations());
      }

      Promise.all(promised).then(() => {
          unblockNavigation();
      });
  };

  return (
    <>
        <EditorPageLayout
            toolbar={{
                saveButton: {
                    hasChanges: hasChanges(),
                    disabled: !hasChanges(),
                    onClick: onSaveClicked,
                },
            }}
        >
            <ControlGroupWithPreview
                hasTopBorder={false}
                preview={<img src={isPortalEdgeSelected ? PolicyConfirmationPreviewPortalEdge : PolicyConfirmationPreview} alt="" />}
            >
                <ListLayout>
                    <FormFieldLine label="Require Confirmation That The Return Fits Your Return Conditions.">
                        <Toggle
                            label=""
                            checked={
                                currentPortalStore.setting
                                    ?.requestCustomersConfirmPolicy
                            }
                            onChange={
                                currentPortalStore.setting
                                    ?.setRequestCustomersConfirmPolicy
                            }
                        />
                    </FormFieldLine>

                    <FormField label="Header">
                        <TranslationInput
                            translationKey={
                                RETURNS_PORTAL_TRANSLATION_KEYS.POLICY_CONFIRMATION_TITLE
                            }
                        />
                    </FormField>

                    <FormField label="Description">
                        <TranslationRichTextEditor
                            minHeight={250}
                            translationKey={
                                RETURNS_PORTAL_TRANSLATION_KEYS.POLICY_CONFIRMATION_DESCRIPTION
                            }
                        />
                    </FormField>

                    <FormField label="Instructions">
                        <TranslationInput
                            translationKey={
                                RETURNS_PORTAL_TRANSLATION_KEYS.POLICY_CONFIRMATION_INSTRUCTIONS
                            }
                        />
                    </FormField>

                    <FormField
                        label="Terms & Conditions To Confirm"
                        instructions="Add bullet points of conditions for customers to confirm. Each bullet point will appear as a checkbox."
                    >
                        <TranslationRichTextEditor
                            minHeight={250}
                            variant={EDITOR_VARIANT.BULLETS_ONLY}
                            translationKey={
                                RETURNS_PORTAL_TRANSLATION_KEYS.POLICY_CONFIRMATION_STATEMENTS
                            }
                        />
                    </FormField>
                </ListLayout>
            </ControlGroupWithPreview>
        </EditorPageLayout>

        {isShowUnsaved && (
            <ConfirmModal
                cancelButton={{
                    title: "Cancel",
                    onClick: () => setIsShowUnsaved(false),
                }}
                okButton={{
                    title: "Discard",
                    onClick: () => {
                        discardChanges();
                        setIsShowUnsaved(false);
                        retryNavigation();
                    },
                }}
            />
        )}
    </>
  );
});

export default PolicyConfirmationEditorContent;